// Audit log utility – stocke les entrées dans localStorage (max 1000)
// et conserve la trace console pour le débogage.

export interface AuditLogEntry {
  id: string;
  action: string;
  userId: string;
  username: string;
  role?: string;
  targetId?: string;
  targetType?: string;
  details?: string;
  timestamp: string;
  ipAddress?: string;
}

export type AuditAction =
  | 'CREATE_USER'
  | 'EDIT_USER'
  | 'DELETE_USER'
  | 'CREATE_PROFILE'
  | 'EDIT_PROFILE'
  | 'DELETE_PROFILE'
  | 'LOGIN'
  | 'LOGOUT'
  | 'CREATE_COMPLAINT'
  | 'EDIT_COMPLAINT'
  | 'CHANGE_STATUS_COMPLAINT'
  | 'OTHER';

const AUDIT_LOG_KEY = 'admin_audit_logs';
const MAX_ENTRIES = 1000;

/** Lit tous les logs stockés dans localStorage */
export function getStoredLogs(): AuditLogEntry[] {
  try {
    const raw = localStorage.getItem(AUDIT_LOG_KEY);
    return raw ? (JSON.parse(raw) as AuditLogEntry[]) : [];
  } catch {
    return [];
  }
}

/** Enregistre une entrée d'audit directement dans localStorage (fallback hors-ligne) */
export function logAuditLocal(entry: Omit<AuditLogEntry, 'id' | 'timestamp'> & { timestamp?: string }) {
  const full: AuditLogEntry = {
    ...entry,
    id: crypto.randomUUID(),
    timestamp: entry.timestamp ?? new Date().toISOString(),
  };
  try {
    const existing = getStoredLogs();
    const updated = [full, ...existing].slice(0, MAX_ENTRIES);
    localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(updated));
  } catch {
    // Si localStorage n'est pas disponible, on ignore silencieusement
  }
  // eslint-disable-next-line no-console
  // console.log('[AUDIT]', JSON.stringify(full));
}

/**
 * Enregistre une entrée d'audit.
 * Utilise le service API si disponible, sinon localStorage.
 * Importer depuis auditLog.service.ts pour la version avec fallback automatique.
 * Cette version est synchrone (localStorage uniquement) — utilisée par le service comme fallback.
 *
 * @deprecated Préférer auditLogService.create() pour le comportement API-first.
 */
export function logAudit(entry: Omit<AuditLogEntry, 'id' | 'timestamp'> & { timestamp?: string }) {
  logAuditLocal(entry);
}

/** Vide le journal d'audit stocké */
export function clearAuditLogs() {
  localStorage.removeItem(AUDIT_LOG_KEY);
}

/** Retourne les logs paginés et filtrés */
export function getPaginatedLogs(
  page: number,
  size: number,
  filters?: { action?: string; username?: string; from?: string; to?: string }
): { data: AuditLogEntry[]; total: number; lastPage: number; page: number } {
  let logs = getStoredLogs();

  if (filters?.action) {
    logs = logs.filter((l) => l.action === filters.action);
  }
  if (filters?.username) {
    const q = filters.username.toLowerCase();
    logs = logs.filter((l) => l.username.toLowerCase().includes(q));
  }
  if (filters?.from) {
    const from = new Date(filters.from).getTime();
    logs = logs.filter((l) => new Date(l.timestamp).getTime() >= from);
  }
  if (filters?.to) {
    const to = new Date(filters.to).getTime();
    logs = logs.filter((l) => new Date(l.timestamp).getTime() <= to);
  }

  const total = logs.length;
  const lastPage = Math.max(1, Math.ceil(total / size));
  const data = logs.slice(page * size, page * size + size);
  return { data, total, lastPage, page };
}
